/**
 * EmptyWorkspace
 *
 * Shown when no artifacts are open - provides quick actions to create new items
 * Optimized for both desktop and mobile with proper touch targets
 */

import React from 'react';
import { useWorkspace } from '../artifacts';

interface QuickActionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

const QuickAction: React.FC<QuickActionProps> = ({ icon, title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex flex-col items-center gap-4 p-6 sm:p-6 rounded-2xl border border-neutral-200 bg-white/90 hover:border-neutral-300 hover:shadow-lg active:scale-[0.98] transition-all duration-200 text-center min-h-[140px] sm:min-h-0"
  >
    <div className="w-14 h-14 sm:w-12 sm:h-12 rounded-xl bg-neutral-100 group-hover:bg-blue-50 flex items-center justify-center text-neutral-500 group-hover:text-blue-600 transition-colors shadow-sm">
      {icon}
    </div>
    <div>
      <h3 className="text-base sm:text-sm font-semibold text-neutral-900 group-hover:text-neutral-950 transition-colors">
        {title}
      </h3>
      <p className="text-sm sm:text-xs text-neutral-500 mt-1">{description}</p>
    </div>
  </button>
);

export const EmptyWorkspace: React.FC = () => {
  const { createDocument } = useWorkspace();

  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-5 sm:px-10 py-8 sm:py-14 bg-white">
      {/* Header */}
      <div className="text-center mb-8 sm:mb-10 space-y-3">
        <div className="inline-flex items-center justify-center w-16 h-16 sm:w-16 sm:h-16 rounded-2xl bg-white text-neutral-700 border border-neutral-200 shadow-sm">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
            />
          </svg>
        </div>
        <h2 className="text-xl sm:text-2xl font-semibold text-neutral-900">Your Workspace</h2>
        <p className="text-sm sm:text-base text-neutral-500 max-w-md mx-auto leading-relaxed">
          Create artifacts.<br></br>The assistant will help you.
        </p>
      </div>

      {/* Quick actions - centered */}
      <div className="w-full">
        <div className="flex justify-center">
          <QuickAction
          icon={
            <svg
              className="w-7 h-7 sm:w-6 sm:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          }
          title="New Document"
          description="Write or generate content."
          onClick={() => createDocument('Untitled Document')}
          />
        </div>
      </div>

      {/* Hint - hide on very small screens to save space */}
      <div className="mt-8 sm:mt-10 text-center">
        <p className="text-xs sm:text-sm text-neutral-500">
          <span className="hidden sm:inline">Tip: </span>Ask the assistant to create artifacts
          for you
        </p>
      </div>
    </div>
  );
};

export default EmptyWorkspace;
